import React from 'react';
import { Chip, Box, Typography } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { Container, FilterChip, FiltersContainer } from './styles';

export interface FilterMapper {
  [key: string]: {
    /** Nome que será exibido para o usuário */
    label: string;
    /** Função para formatar o valor do filtro */
    formatValue?: (value: any) => string;
    /** Se este filtro deve ser ignorado (não exibido) */
    hidden?: boolean;
    /** Ícone opcional para o filtro */
    icon?: React.ReactNode;
  };
}

export interface ActiveFiltersProps {
  /** Parâmetros ativos da tela */
  params: Record<string, any>;
  /** Mapeamento dos parâmetros para exibição */
  mapper: FilterMapper;
  /** Função chamada quando um filtro é removido */
  onRemoveFilter: (filterKey: string) => void;
  /** Função chamada quando todos os filtros são limpos */
  onClearAll?: () => void;
  /** Se deve mostrar botão "Limpar todos" */
  showClearAll?: boolean;
  /** Título da seção */
  title?: string;
  /** Se deve esconder quando não há filtros */
  hideWhenEmpty?: boolean;
}

export const ActiveFilters = ({
  params,
  mapper,
  onRemoveFilter,
  onClearAll,
  showClearAll = true,
  title = "Filtros ativos",
  hideWhenEmpty = true,
}: ActiveFiltersProps) => {
  // Filtra parâmetros que devem ser exibidos
  const visibleFilters = Object.entries(params).filter(([key, value]) => {
    const mapperConfig = mapper[key];

    // Ignora se não está no mapper ou está marcado como hidden
    if (!mapperConfig || mapperConfig.hidden) {
      return false;
    }

    // Ignora valores vazios/nulos/undefined
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // Ignora arrays vazios
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }

    return true;
  });

  // Se deve esconder quando vazio e não há filtros
  if (hideWhenEmpty && visibleFilters.length === 0) {
    return null;
  }

  const formatFilterValue = (key: string, value: any): string => {
    const mapperConfig = mapper[key];

    if (mapperConfig?.formatValue) {
      return mapperConfig.formatValue(value);
    }

    // Formatação padrão para arrays
    if (Array.isArray(value)) {
      return value.length === 1 ? String(value[0]) : `${value.length} selecionados`;
    }

    return String(value);
  };

  const handleRemoveFilter = (filterKey: string) => {
    onRemoveFilter(filterKey);
  };

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
    }
  };

  return (
    <Container>
      {title && (
        <Typography variant="subtitle2" sx={{ color: '#666', marginBottom: '8px', fontWeight: 500 }}>
          {title}
        </Typography>
      )}

      <FiltersContainer>
        {visibleFilters.map(([key, value]) => {
          const mapperConfig = mapper[key];
          const displayLabel = mapperConfig.label;
          const displayValue = formatFilterValue(key, value);

          return (
            <FilterChip
              key={key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {mapperConfig.icon && (
                    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                      {mapperConfig.icon}
                    </Box>
                  )}
                  <span>
                    <strong>{displayLabel}:</strong> {displayValue}
                  </span>
                </Box>
              }
              onDelete={() => handleRemoveFilter(key)}
              deleteIcon={<IoClose size={16} />}
              size="medium"
            />
          );
        })}

        {showClearAll && visibleFilters.length > 1 && (
          <Chip
            label="Limpar todos"
            onClick={handleClearAll}
            size="small"
            variant="outlined"
            sx={{
              color: '#89BD23',
              borderColor: '#89BD23',
              '&:hover': {
                backgroundColor: 'rgba(137, 189, 35, 0.08)',
                borderColor: '#7AA61F',
              },
            }}
          />
        )}
      </FiltersContainer>
    </Container>
  );
};
